import { useMemo, type ReactNode } from 'react'
import { useBrandKit } from '../brand/BrandKitContext.tsx'
import type { DocControlEntry } from '../lib/api.ts'
import { dropLeadingBlockquote, extractOutline, wrapTables } from '../lib/doc-structure.ts'
import { copyText, downloadDoc, renderPrintBlanks } from '../lib/export.ts'
import { renderMarkdown } from '../lib/markdown.ts'
import { Breadcrumbs, type Crumb } from './Breadcrumbs.tsx'
import { DocHeader } from './DocHeader.tsx'
import { DocRail, type RelatedDoc } from './DocRail.tsx'
import { DraftBanner } from './DraftBanner.tsx'

/**
 * Shared presentational shell for a whole-loaded document: the document page,
 * knowledge test and meeting guide differ only in which `api` call feeds them,
 * so the layout — draft gate, masthead, body, contents rail, disclaimer —
 * lives once here rather than being copy-pasted three times.
 */
export function RenderedDoc({
  title, markdown, disclaimer, reviewStatus, badges, docControl, related, crumbs, extra,
}: {
  title: string
  markdown: string
  disclaimer: string
  reviewStatus: string
  badges?: string[]
  docControl?: DocControlEntry[]
  related?: RelatedDoc[]
  crumbs?: Crumb[]
  extra?: ReactNode
}) {
  const { brand } = useBrandKit()
  // Fail safe: anything other than the exact 'approved' string is treated as
  // draft, so an unrecognised or future review status still shows the gate.
  const isDraft = reviewStatus !== 'approved'

  const { downloadHtml, screenHtml, outline } = useMemo(() => {
    // Print blanks ([[Full Name]] etc.) must render as a labelled fillable rule
    // on screen too, not just in the exported .doc — a raw [[bracket]] reads as
    // a templating bug on a form someone is about to read and sign.
    const full = renderPrintBlanks(renderMarkdown(markdown))
    // The download keeps the server's baked-in draft notice: a .doc that leaves
    // this app carries its own warning or it carries none at all. On screen
    // DraftBanner already says it, so the copy in the body would be the second.
    const body = wrapTables(isDraft ? dropLeadingBlockquote(full) : full)
    const { html, outline: items } = extractOutline(body)
    return { downloadHtml: full, screenHtml: html, outline: items }
  }, [markdown, isDraft])

  return (
    <main className="tool">
      {/* First thing on the page, above even the trail: a strip across the top
          of the column, so the warning is read before anything it applies to. */}
      {isDraft && <DraftBanner />}

      {crumbs && <Breadcrumbs items={crumbs} />}

      <DocHeader
        title={title}
        badges={badges}
        docControl={docControl}
        actions={
          <>
            <button type="button" className="btn btn--soft" onClick={() => copyText(markdown)}>Copy</button>
            <button type="button" className="btn btn--soft" onClick={() => downloadDoc(title, brand, downloadHtml)}>
              Download .doc
            </button>
          </>
        }
      />

      <div className="doc-layout">
        <div className="doc-layout__body">
          <article className="document" dangerouslySetInnerHTML={{ __html: screenHtml }} />
          {extra && <div className="tool__extra">{extra}</div>}
        </div>

        <DocRail outline={outline} related={related} />
      </div>

      {/* Server-enforced: always rendered, unconditionally — never gated on the
          string being non-empty. The guarantee must live in this markup, not
          in "the server always sends one today"; do not reinstate a guard. */}
      <p className="disclaimer">{disclaimer}</p>
    </main>
  )
}
